import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "mapbox-gl/dist/mapbox-gl.css";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";

import LoadingModal from "../../common/components/LoadingModal";
import { get, post, unwrapError } from "../../common/utils/api";
import {
  addSentinel2Tiles,
  getBounds,
  mapboxDrawStyle,
  setSentinel2Visibility,
} from "../../common/utils/map.js";
import Map from "../components/Map";
import Palette from "../components/Palette";
import { categories, nameToColor } from "../utils/map.js";

const DW_GEOJSON_BASE_URL =
  "https://dw-labelling-datacollection.s3.us-east-1.amazonaws.com";

const POLYGON_FILL_LAYER_IDS = [
  "gl-draw-polygon-fill-inactive",
  "gl-draw-polygon-fill-active",
  "gl-draw-polygon-fill-static",
  "gl-draw-polygon-color-picker",
];
const DRAW_SOURCE_BUCKETS = ["cold", "hot"];

const emptyFeatureCollection = () => ({
  type: "FeatureCollection",
  features: [],
});

async function getInitialFeatures(dwId) {
  if (!dwId) return emptyFeatureCollection();

  const response = await fetch(
    `${DW_GEOJSON_BASE_URL}/${encodeURIComponent(dwId)}.geojson`,
  );
  if (!response.ok) {
    throw new Error(`GeoJSON request failed with status ${response.status}`);
  }

  const geojson = await response.json();
  if (
    geojson.type !== "FeatureCollection" ||
    !Array.isArray(geojson.features)
  ) {
    throw new Error("GeoJSON response is not a FeatureCollection");
  }

  return {
    ...geojson,
    features: geojson.features.map((feature) => {
      const label = feature.properties?.properties?.val;
      return {
        ...feature,
        properties: {
          portColor: nameToColor[label],
        },
      };
    }),
  };
}

function hasValidPolygonGeometry(feature) {
  const { coordinates, type } = feature.geometry || {};

  if (type === "Polygon") {
    return Array.isArray(coordinates?.[0]) && coordinates[0].length >= 4;
  }

  if (type === "MultiPolygon") {
    return (
      Array.isArray(coordinates) &&
      coordinates.length > 0 &&
      coordinates.every(
        (polygon) => Array.isArray(polygon?.[0]) && polygon[0].length >= 4,
      )
    );
  }

  return false;
}

function Landcover4Task({ showAlert, t }) {
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [currentScene, setCurrentScene] = useState();
  const [sceneVisible, setSceneVisible] = useState(true);
  const [isUpdating, setIsUpdating] = useState(true);
  const [polygonOpacity, setPolygonOpacity] = useState(50);

  const map = useRef(null);
  const categoryRef = useRef();

  const [task, setTask] = useState(null);
  const [initialFeatures, setInitialFeatures] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const draw = useMemo(() => {
    return new MapboxDraw({
      displayControlsDefault: false,
      boxSelect: true,
      // Select which mapbox-gl-draw control buttons to add to the map.
      controls: {
        polygon: true,
        trash: true,
      },
      defaultMode: "simple_select",
      userProperties: true,
      styles: mapboxDrawStyle,
    });
  }, []);

  function updateScenes(bbox, date) {
    const startDate = moment(date).subtract(1, "months").toISOString();
    const endDate = moment(date).add(1, "months").toISOString();

    const searchQuery = {
      datetime: `${startDate}/${endDate}`,
      limit: 200,
      collections: ["super-resolution-sentinel2_l2a_cog"],
      bbox: bbox,
    };
    const superResolutionHref =
      "https://stac-server-dev.groundtruthanalytics.com/search";
    const backupQuery = {
      ...searchQuery,
      collections: ["sentinel-2-l2a"],
    };
    const backupHref = "https://earth-search.aws.element84.com/v1/search";

    function search(href, query) {
      return fetch(href, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(query),
      }).then((res) => res.json());
    }

    async function runSearch() {
      let data = await search(superResolutionHref, searchQuery);

      if (!data.features || data.features.length === 0) {
        console.log("Super resolution search empty, try sentinel-2...");
        data = await search(backupHref, backupQuery);
      }

      const scenes = data.features.map((f) => {
        return {
          date: f.properties.start_datetime || f.properties.datetime,
          scene_id: f.id,
        };
      });
      // Ascending
      scenes.sort(function (a, b) {
        return new Date(a.date) - new Date(b.date);
      });

      // Find closest date to input
      const targetDate = moment(date).toDate();
      let closestIndex;
      let closestDiff = Infinity;
      scenes.forEach((d, i) => {
        const diff = Math.abs(new Date(d.date) - targetDate);
        if (diff < closestDiff) {
          closestIndex = i;
          closestDiff = diff;
        }
      });

      setScenes(scenes);
      setCurrentScene(closestIndex);
      setSceneVisible(true);
    }

    runSearch().catch(console.error);
  }

  useEffect(() => {
    if (mapLoaded) {
      setSentinel2Visibility(map.current, sceneVisible);
    }
  }, [sceneVisible, mapLoaded]);

  function deleteAllParcels() {
    if (!mapLoaded || isUpdating || !task) return;
    draw.changeMode("simple_select");
    draw.deleteAll();
  }

  async function submit() {
    const parcels = [];
    draw.getAll().features.forEach((f) => {
      // Ignore Mapbox Draw's unfinished/phantom geometry.
      if (hasValidPolygonGeometry(f)) {
        // Decode the portColor into landcover label
        let color = f.properties.portColor;
        f.properties.label = categories.find((c) => c.color === color)?.name;
        delete f.properties.portColor;

        parcels.push({ data: f });
      }
    });
    const payload = {
      latitude: task.latitude,
      longitude: task.longitude,
      width: task.width,
      scene_datetime: task.scene_datetime,
      dw_id: task.dw_id,
      group: task.group,
      landcover4_submission_set: parcels,
    };
    return post("/landcover4/task/submit/", payload)
      .then(() => {
        draw.deleteAll();
        draw.changeMode("simple_select");
        showAlert({
          type: "success",
          message: t("common.successfullySubmitted", "Successfully submitted"),
        });
      })
      .catch(function (error) {
        showAlert({ type: "error", message: unwrapError(error) });
      });
  }

  const getTask = async () => {
    return get(
      `/landcover4/task/?group=${encodeURIComponent(selectedGroup)}`,
    ).then((response) => response.data);
  };

  const nextTask = async (skip) => {
    setIsUpdating(true);
    if (task && !skip) {
      await submit();
    } else if (task) {
      draw.deleteAll();
      draw.changeMode("simple_select");
    }

    const newTask = await getTask();
    const bounds = getBounds(newTask);
    updateScenes(bounds.flat(), newTask.scene_datetime);

    let features = emptyFeatureCollection();
    try {
      features = await getInitialFeatures(newTask.dw_id);
    } catch (error) {
      showAlert({
        type: "error",
        message: `Could not load initial map features: ${error.message}`,
      });
    }

    setInitialFeatures({ dwId: newTask.dw_id, geojson: features });
    setTask(newTask);
    setIsUpdating(false);
  };
  useEffect(() => {
    if (selectedGroup !== null) {
      nextTask(true);
    }
  }, [selectedGroup]);

  const getGroups = async () => {
    try {
      const response = await get("/landcover4/candidate/groups/");
      const groups = response.data;
      setGroupOptions(groups);

      if (groups.length > 0) {
        setSelectedGroup(groups[0]);
      } else {
        setIsUpdating(false);
        showAlert({
          type: "error",
          message: "No landcover groups are available.",
        });
      }
    } catch (error) {
      setIsUpdating(false);
      showAlert({ type: "error", message: unwrapError(error) });
    }
  };
  useEffect(() => {
    getGroups();
  }, []);

  useEffect(() => {
    if (!mapLoaded || !task) return;
    if (!initialFeatures || initialFeatures.dwId !== task.dw_id) return;

    draw.set(initialFeatures.geojson);
    draw.changeMode("simple_select");
  }, [draw, initialFeatures, mapLoaded, task]);

  useEffect(() => {
    if (!mapLoaded) return;

    POLYGON_FILL_LAYER_IDS.forEach((baseLayerId) => {
      DRAW_SOURCE_BUCKETS.forEach((sourceBucket) => {
        const layerId = `${baseLayerId}.${sourceBucket}`;
        if (map.current.getLayer(layerId)) {
          map.current.setPaintProperty(
            layerId,
            "fill-opacity",
            polygonOpacity / 100,
          );
        }
      });
    });
  }, [mapLoaded, polygonOpacity]);

  const isEnd = currentScene === scenes.length - 1;
  function previousScene() {
    if (currentScene !== 0) {
      setCurrentScene(currentScene - 1);
      setSceneVisible(true);
    }
  }
  function nextScene() {
    if (!isEnd) {
      setCurrentScene(currentScene + 1);
      setSceneVisible(true);
    }
  }

  useEffect(() => {
    if (mapLoaded && currentScene) {
      const sceneId = scenes[currentScene].scene_id;
      addSentinel2Tiles(map.current, sceneId);
    }
  }, [mapLoaded, currentScene, scenes]);

  return (
    <React.Fragment>
      <LoadingModal open={isUpdating} />
      <div className="border-b border-gray-200 bg-white px-3 py-3 sm:px-6 rounded-md">
        <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
          <div className="ml-4 mt-2">
            <h3 className="text-md font-medium leading-6 text-gray-900">
              Click the map to draw polygons that correspond to plots
            </h3>
          </div>
          {groupOptions.length > 0 && selectedGroup !== null && (
            <div className="ml-4 mt-2 flex items-baseline">
              Group:
              <select
                className="ml-2 max-w-lg rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:max-w-xs sm:text-sm"
                value={selectedGroup}
                onChange={(e) => {
                  setSelectedGroup(e.target.value);
                }}
              >
                {groupOptions.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="ml-4 mt-2 flex-shrink-0">
            <div className="flex items-baseline">
              <div className="flex">
                {task?.date && `Date: ${task.date.substring(0, 10)},`}
              </div>

              <div className="ml-2 flex">
                {task &&
                  `Location (${task.longitude.toFixed(
                    4,
                  )}, ${task.latitude.toFixed(4)})`}
              </div>
              {task?.dw_id && (
                <a
                  href={`${DW_GEOJSON_BASE_URL}/${encodeURIComponent(
                    task.dw_id,
                  )}.png`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 ml-4"
                >
                  View timeseries
                </a>
              )}
              <button
                type="button"
                onClick={deleteAllParcels}
                disabled={!mapLoaded || isUpdating || !task}
                className="inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ml-4"
              >
                {t("common.deleteAllParcels", "Delete all")}
              </button>
              <button
                type="button"
                onClick={() => nextTask(true)}
                className="inline-flex items-center rounded-md border border-transparent border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 ml-4"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={() => nextTask(false)}
                className="inline-flex items-center rounded-md border border-transparent bg-cyan-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ml-4"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
      {task && (
        <div className="mt-6 flex flex-row justify-center">
          <Map
            map={map}
            interactive={true}
            task={task}
            draw={draw}
            categoryRef={categoryRef}
            mapLoaded={mapLoaded}
            setMapLoaded={setMapLoaded}
          />
          <div className="mt-0 ml-6 flex-shrink-0 flex-grow-0">
            <div className="mb-6 px-3 w-48">
              <button
                type="button"
                onClick={() => draw.changeMode("simple_select")}
                disabled={!mapLoaded || isUpdating}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("common.selectPolygons", "Select polygons")}
              </button>
              <p className="mt-2 text-sm text-gray-500">
                {t(
                  "common.selectPolygonsHelp",
                  "Hold Shift and drag a box to select multiple polygons. Shift-click to add or remove a polygon. Use the trash control to delete the selection.",
                )}
              </p>
            </div>
            <Palette categoryRef={categoryRef} interactive={true} />
            <div className="mt-6 px-3 w-48">
              <label
                htmlFor="polygon-opacity"
                className="flex justify-between text-sm font-medium text-gray-500"
              >
                <span>Polygon opacity</span>
                <span>{polygonOpacity}%</span>
              </label>
              <input
                id="polygon-opacity"
                type="range"
                min="0"
                max="100"
                step="5"
                value={polygonOpacity}
                onChange={(event) =>
                  setPolygonOpacity(Number(event.target.value))
                }
                className="mt-2 w-full accent-cyan-600"
              />
            </div>
          </div>
        </div>
      )}
      {scenes.length > 0 && (
        <div className="text-center">
          <div className="mt-8 border-b border-gray-200 bg-white px-3 py-3 sm:px-6 rounded-md inline-flex">
            <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-center sm:flex-nowrap">
              <div className="ml-4 mt-2 flex items-center">
                Real-time satellite image date:
                <div className="flex items-center ml-2">
                  <div>
                    <button
                      type="button"
                      onClick={previousScene}
                      disabled={currentScene === 0}
                      className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-50"
                    >
                      <span className="sr-only">Previous</span>

                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="flex justify-center">
                    <div className="relative border-y border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500 focus:z-20 w-32 text-center">
                      {moment(scenes[currentScene].date).format("YYYY-MM-DD")}
                    </div>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={nextScene}
                      disabled={isEnd}
                      className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-50"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
              </div>
              <div className="ml-8 mt-2 flex-shrink-0">
                <div className="flex items-baseline space-x-2">
                  <div className="flex">Show real-time satellite image:</div>
                  <input
                    id="comments"
                    aria-describedby="comments-description"
                    name="comments"
                    type="checkbox"
                    checked={sceneVisible}
                    onChange={() => setSceneVisible((visible) => !visible)}
                    className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default withTranslation()(Landcover4Task);
